import React, { useState, useRef, useEffect } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { cn, inputSurface } from "@design-system";

export interface EditableDropdownItem {
  key: string;
  label: string;
  value: string;
}

export interface EditableDropdownProps {
  value: number;
  onChange: (value: number) => void;
  items: EditableDropdownItem[];
  placeholder?: string;
  min?: number;
  step?: number;
  suffix?: string;
  className?: string;
}

export const EditableDropdown: React.FC<EditableDropdownProps> = ({
  value,
  onChange,
  items,
  placeholder = "Skriv eller velg",
  min = 1,
  step = 1,
  suffix = "",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 240; // max-h-60 = 240px
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Allow empty string or valid positive integers
    if (newValue === '' || /^[1-9]\d*$/.test(newValue)) {
      setInputValue(newValue);
      
      // Only call onChange if value is valid
      if (newValue !== '') {
        const numValue = parseInt(newValue);
        if (numValue >= min) {
          onChange(numValue);
        }
      }
    }
  };

  const handleInputBlur = () => {
    // If input is empty, reset to current value
    if (inputValue === '') {
      setInputValue(value.toString());
    }
  };

  const handleDropdownSelect = (selectedValue: string) => {
    const numValue = parseInt(selectedValue);
    setInputValue(selectedValue);
    onChange(numValue);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(inputSurface, "block w-full p-2.5 pr-10 cursor-pointer focus:ring-blue-500 focus:border-blue-500")}
          min={min}
          step={step}
        />
        {suffix && (
          <span className="absolute right-10 top-1/2 transform -translate-y-1/2 text-muted pointer-events-none">
            {suffix}
          </span>
        )}
        <button
          type="button"
          onClick={toggleDropdown}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 dark:text-muted dark:hover:text-gray-300"
          tabIndex={-1}
        >
          <HiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div className={`absolute z-10 w-full ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-600 max-h-60 overflow-y-auto`}>
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => handleDropdownSelect(item.value)}
              className="w-full px-3 py-2 text-left text-gray-600 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}; 